import { message } from "antd";
import { Driver } from "../services/driver/index1";
import fileServices from "../services/storage/index1";
import { localErrorHandler } from "../utils/errorHandler";
import * as _ from "lodash";
import { CustomerService } from "../services/customers";

const namespace = "driver";

const initialState = {
  list: [],
  pagination: {
    total: 0,
  },
  filters: {},
  currentDriver: null,
  loading: {
    fetch: false,
    add: false,
    update: false,
    delete: false,
    fetchById: false,
    updateBalance: false,
    activate: false,
    deactivate: false,
  },
};

export const fetchDrivers = (params) => ({
  type: `${namespace}/fetchDrivers`,
  payload: params,
});

export const addDriver = (data) => ({
  type: `${namespace}/addDriver`,
  payload: data,
});

export const updateDriver = (driverId, data) => ({
  type: `${namespace}/updateDriver`,
  payload: { driverId, data },
});

export const deleteDriver = (driverId) => ({
  type: `${namespace}/deleteDriver`,
  payload: driverId,
});

export const getDriverById = (driverId) => ({
  type: `${namespace}/getDriverById`,
  payload: driverId,
});

export const updateDriverBalance = (driverId, data) => ({
  type: `${namespace}/updateDriverBalance`,
  payload: { driverId, data },
});

export const activateDriver = (driverId) => ({
  type: `${namespace}/activateDriver`,
  payload: driverId,
});

export const deactivateDriver = (driverId) => ({
  type: `${namespace}/deactivateDriver`,
  payload: driverId,
});

export default {
  namespace,
  state: initialState,
  effects: {
    *activateDriver({ payload }, { call, put }) {
      try {
        yield put({ type: "startLoading", loadingType: "activate" });
        const response = yield call(CustomerService.activateUser, payload);
        yield put({
          type: "updateDrivers",
          payload: response.data,
        });
        message.success("Driver activated successfully");
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "activate" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "activate" });
      }
    },
    *deactivateDriver({ payload }, { call, put }) {
      try {
        yield put({ type: "startLoading", loadingType: "deactivate" });
        const response = yield call(CustomerService.deactivateUser, payload);
        yield put({
          type: "updateDrivers",
          payload: response.data,
        });
        message.success("Driver deactivated successfully");
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "deactivate" });
      } finally {
        yield put({ type: "stopLoading", loadingType: "deactivate" });
      }
    },
    *fetchDrivers({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "fetch" });
      try {
        const response = yield call(Driver.getDrivers, payload);

        yield put({
          type: "setDrivers",
          payload: {
            list: response.data.list,
            pagination: {
              total: response.data.total || 0,
            },
            filters: payload,
          },
        });
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "fetch" });
      }
      yield put({ type: "stopLoading", loadingType: "fetch" });
    },

    *addDriver({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "add" });
      try {
        let updatedData = { ...payload };
        if (payload.photo) {
          const { url } = yield call(fileServices.uploadFile, {
            file: payload.photo,
          });

          updatedData = {
            ..._.omit(payload, ["photo"]),
            photo: url,
          };
        }
        yield call(Driver.addDriver, updatedData);
        message.success("Driver added successfully");
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "add" });
      }
      yield put({ type: "stopLoading", loadingType: "add" });
    },

    *updateDriver({ payload }, { call, put }) {
      const { data, driverId } = payload;
      yield put({ type: "startLoading", loadingType: "update" });

      try {
        let updatedData = { ...data };

        const isUploadingNewPhoto = !!data.photo;
        const isDeletingOldPhotoOnly = !!data.photoUrl && !data.photo;
        if (isUploadingNewPhoto) {
          const { url } = yield call(fileServices.uploadFile, {
            file: data.photo,
          });
          updatedData.photo = url;
        }
        if (data.photoUrl) {
          yield call(fileServices.deleteFile, data.photoUrl);

          if (isDeletingOldPhotoOnly) {
            updatedData.photo = "";
          }
        }
        updatedData = _.omit(updatedData, ["photoUrl"]);

        yield call(Driver.updatDriver, updatedData, driverId);

        message.success("Driver updated successfully");
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "update" });
      }

      yield put({ type: "stopLoading", loadingType: "update" });
    },

    *deleteDriver({ payload }, { call, put }) {
      yield put({ type: "startLoading", loadingType: "delete" });
      try {
        yield call(Driver.deleteDriver, payload);
        yield put({ type: "fetchDrivers" });
        message.success("Driver deleted successfully");
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "delete" });
      }
      yield put({ type: "stopLoading", loadingType: "delete" });
    },

    *getDriverById({ payload }, { call, put }) {
      try {
        yield put({ type: "startLoading", loadingType: "fetchById" });
        const response = yield call(Driver.getDriverById, payload);
        yield put({ type: "setCurrentDriver", payload: response.data });
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "fetchById" });
      }
      yield put({ type: "stopLoading", loadingType: "fetchById" });
    },

    *updateDriverBalance({ payload }, { call, put }) {
      const { driverId, data } = payload;
      try {
        yield put({ type: "startLoading", loadingType: "updateBalance" });
        const response = yield call(Driver.updateDriverBalance, driverId, data);

        yield put({
          type: "updateDriversList",
          payload: { driverId, data: response.data },
        });

        message.success("Driver balance updated successfully");
      } catch (error) {
        localErrorHandler({ namespace, error, stopLoading: "updateBalance" });
      }
      yield put({ type: "stopLoading", loadingType: "updateBalance" });
    },
  },

  reducers: {
    updateDrivers(state, { payload }) {
      const updatedList = state.list.map((driver) =>
        driver.user.id === payload.id ? { ...driver, user: payload } : driver
      );
      return {
        ...state,
        list: updatedList,
      };
    },
    setDrivers(state, { payload }) {
      return {
        ...state,
        list: payload.list,
        pagination: payload.pagination,
        filters: payload.filters,
      };
    },
    setCurrentDriver(state, { payload }) {
      return {
        ...state,
        currentDriver: payload,
      };
    },
    updateDriversList(state, { payload }) {
      const { driverId, data } = payload;
      const driverList = [...state.list];
      const index = driverList.findIndex((driver) => driver.id === driverId);
      if (index !== -1) {
        driverList[index] = data;
        return {
          ...state,
          list: driverList,
        };
      }

      return state;
    },
    startLoading(state, { loadingType }) {
      return {
        ...state,
        loading: { ...state.loading, [loadingType]: true },
      };
    },
    stopLoading(state, { loadingType }) {
      return {
        ...state,
        loading: { ...state.loading, [loadingType]: false },
      };
    },
  },
};
